import { Label } from "@radix-ui/react-label";
import axios from "axios";
import moment from "moment";
import React, { useState } from "react";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../Components/Global/UI/Dialog";
import { BASE_URL } from "../../Screens/Settings/DASHBOARD_SETTINGS";
import { MapboxSearchBox } from "../Global/MapBoxAutoComplete";
import { Button } from "../Global/UI/Button";
import { Input } from "../Global/UI/Input";

const JobCard = ({ job, onJobDataChange, onRemoveJob, onAddReturnJob }) => {
  const [isLogOpen, setIsLogOpen] = useState(false);

  // --- FIX 1: Create a single, reliable variable for the unique ID ---
  // This will use the database '_id' if it exists, otherwise it falls back to the client-side 'id'.
  const uniqueJobId = job._id || job.id;

  const calculateDistance = async (pickupAddress, dropOffAddress) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/api/settings/locations/calculateDistances`,
        {
          pickupAddress: pickupAddress,
          dropOffAddress: dropOffAddress,
        }
      );
      return response.data.distance;
    } catch (error) {
      console.error("Error calculating distance:", error);
      return "";
    }
  };

  const handleCreateReturn = async () => {
    const returnJob = {
      ...job,
      // --- FIX 2: Generate a new, truly unique ID for the return job ---
      id: `client_return_${Date.now()}`,
      startingAddress: job.endingAddress,
      endingAddress: job.startingAddress,
      miles: job.miles || "",
      referenceNumber: job.referenceNumber || "",
    };
    onAddReturnJob(returnJob);
  };

  return (
    <div className="border p-4 my-4 flex flex-col">
      <div className="flex flex-row justify-between w-full">
        <div></div>
        <div className="flex flex-row gap-4">
          <Dialog open={isLogOpen} onOpenChange={setIsLogOpen}>
            <DialogTrigger asChild>
              <Button>View Logs</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Job Timestamps</DialogTitle>
                <DialogDescription>
                  Below are the timestamps recorded for this job.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div>
                  <strong>At Starting Location:</strong>{" "}
                  {job.timestamps?.atStartingLocation
                    ? moment(job.timestamps.atStartingLocation).format(
                        "DD/MM/YYYY HH:mm:ss"
                      )
                    : "Not recorded"}
                </div>
                <div>
                  <strong>Left Starting Location:</strong>{" "}
                  {job.timestamps?.leftStartingLocation
                    ? moment(job.timestamps.leftStartingLocation).format(
                        "DD/MM/YYYY HH:mm:ss"
                      )
                    : "Not recorded"}
                </div>
                <div>
                  <strong>Arrived at Destination:</strong>{" "}
                  {job.timestamps?.arrivedDestination
                    ? moment(job.timestamps.arrivedDestination).format(
                        "DD/MM/YYYY HH:mm:ss"
                      )
                    : "Not recorded"}
                </div>
                <div>
                  <strong>Cleared:</strong>{" "}
                  {job.timestamps?.cleared
                    ? moment(job.timestamps.cleared).format(
                        "DD/MM/YYYY HH:mm:ss"
                      )
                    : "Not recorded"}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* --- FIX 3: Use the correct uniqueJobId for all actions --- */}
          <button onClick={() => onRemoveJob(uniqueJobId)}>X</button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 items-center mb-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`patientName-${uniqueJobId}`}>Patient Name</Label>
          <Input
            id={`patientName-${uniqueJobId}`}
            type="text"
            value={job.patientName}
            onChange={(e) =>
              onJobDataChange(uniqueJobId, "patientName", e.target.value)
            }
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`dob-${uniqueJobId}`}>Patient DOB</Label>
          <Input
            id={`dob-${uniqueJobId}`}
            type="date"
            value={
              job.patientDOB
                ? moment(job.patientDOB, "DD/MM/YYYY").format("YYYY-MM-DD")
                : ""
            }
            onChange={(e) =>
              onJobDataChange(
                uniqueJobId,
                "patientDOB",
                moment(e.target.value, "YYYY-MM-DD").format("DD/MM/YYYY")
              )
            }
            placeholder="DD/MM/YYYY"
            className="w-full"
          />
        </div>
        <div className="grid w-full max-w-sm gap-1.5">
          <Label htmlFor={`startingAddress-${uniqueJobId}`}>
            Starting Address
          </Label>
          <MapboxSearchBox
            value={job.startingAddress}
            onChange={(val) =>
              onJobDataChange(uniqueJobId, "startingAddress", val)
            }
            onPlaceSelect={async (place) => {
              const fullAddress =
                place?.properties?.formatted ||
                place?.properties?.address_line1 ||
                place?.properties?.name ||
                "";
              onJobDataChange(uniqueJobId, "startingAddress", fullAddress);
              if (job.endingAddress && fullAddress) {
                const miles = await calculateDistance(
                  fullAddress,
                  job.endingAddress
                );
                onJobDataChange(uniqueJobId, "miles", miles);
              }
            }}
          />
        </div>
        <div className="grid w-full max-w-sm gap-1.5">
          <Label htmlFor={`endingAddress-${uniqueJobId}`}>Ending Address</Label>
          <MapboxSearchBox
            value={job.endingAddress}
            onChange={(val) =>
              onJobDataChange(uniqueJobId, "endingAddress", val)
            }
            onPlaceSelect={async (place) => {
              const fullAddress =
                place?.properties?.formatted ||
                place?.properties?.address_line1 ||
                place?.properties?.name ||
                "";
              onJobDataChange(uniqueJobId, "endingAddress", fullAddress);
              if (job.startingAddress && fullAddress) {
                const miles = await calculateDistance(
                  job.startingAddress,
                  fullAddress
                );
                onJobDataChange(uniqueJobId, "miles", miles);
              }
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`miles-${uniqueJobId}`}>Miles</Label>
          <Input
            type="text"
            id={`miles-${uniqueJobId}`}
            value={job.miles}
            onChange={(e) =>
              onJobDataChange(uniqueJobId, "miles", e.target.value)
            }
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`referenceNumber-${uniqueJobId}`}>
            Reference Number
          </Label>
          <Input
            type="text"
            id={`referenceNumber-${uniqueJobId}`}
            value={job.referenceNumber}
            onChange={(e) =>
              onJobDataChange(uniqueJobId, "referenceNumber", e.target.value)
            }
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`notes-${uniqueJobId}`}>Notes</Label>
          <Input
            type="text"
            id={`notes-${uniqueJobId}`}
            value={job.notes}
            onChange={(e) =>
              onJobDataChange(uniqueJobId, "notes", e.target.value)
            }
          />
        </div>
      </div>
    </div>
  );
};

export default JobCard;